import fp from 'lodash/fp';
import clsx from 'clsx';
import React, { ComponentType, Consumer, SyntheticEvent, ElementType, MutableRefObject } from 'react';
import { config } from '@factorin/container.macro';

import styles from './utils.module.scss';


export const { grabCursor } = styles;

export const animation: number = 300;

export const stopPropagation = (event: SyntheticEvent) =>
    event && event.stopPropagation();

export type FactorinCheckModel = {
    all: boolean;
    include: number[];
    exclude: number[];
};

export const DEFAULT_CHECK_MODEL: FactorinCheckModel = {
    all: false,
    include: [],
    exclude: [],
};

export const setHtml = (html: string) => ({
    dangerouslySetInnerHTML: { __html: html },
});

export const setRef = <TRef extends any>(ref: React.Ref<TRef>, value: TRef): void => {
    if (ref && fp.isFunction(ref))
        ref(value);
    if (ref && 'current' in ref) {
        const mutableRef = ref as MutableRefObject<TRef>;
        mutableRef.current = value;
    }
};

export const multipleRef = <TRef extends any>(...refs: React.Ref<TRef>[]) =>
    (value: TRef): void =>
        refs.forEach(ref => setRef(ref, value));

export const getDisplayName = (...args: ComponentType[]) => args
    .map(Component => Component.displayName || Component.name || 'Component')
    .join(', ');

export type Omit<TSource, KOmitted extends keyof TSource> =
    Pick<TSource, Exclude<keyof TSource, KOmitted>>;

export const contextHOC =
    <PContext extends {}>
    (Context: Consumer<PContext>) =>
        <PComponent extends PContext>(
            Component: ComponentType<PComponent>,
        ): ComponentType<Omit<PComponent, keyof PContext>> =>
            (props: Omit<PComponent, keyof PContext>) => (
                <Context>
                    {value => {
                        const allProps = { ...value, ...props } as PComponent;
                        return <Component {...allProps} />;
                    }}
                </Context>
            );

export interface ClassName {
    className?: string;
}

export const withClassName = <PComponent extends ClassName>(
    className: string,
    component: ElementType<PComponent>,
): ComponentType<PComponent> => ({ className: additionalClassName, ...props }) =>
    React.createElement(component, {
        ...props,
        className: clsx(className, additionalClassName),
    } as PComponent);

export type Forbid<TType, TForbid> = TType & {
    [K in keyof TForbid]?: never;
};

export type ComponentWrapper<PComponent, PWrapper> = PWrapper & {
    component: ElementType<Forbid<PComponent, PWrapper>>;
    componentRef?: React.Ref<any>;
};

export type ComponentWrapperProps<PComponent, PWrapper> =
    PComponent & ComponentWrapper<PComponent, PWrapper>;

export { clsx as cn };

type LocalStorageMappers<TItem> = {
    serialize: (item: TItem) => string;
    deserialize: (saved: string) => TItem;
    fallback?: TItem | (() => TItem);
};

type LocalStorageAccessors<TItem> = {
    get: () => TItem;
    set: (value: TItem) => void;
};

interface LocalStorageAccessor {
    <TItem extends any>(
        key: string,
        mappers: LocalStorageMappers<TItem>
    ): LocalStorageAccessors<TItem>;
    (
        key: string,
        mappers?: Partial<LocalStorageMappers<string>>
    ): LocalStorageAccessors<string>;
}

export const localStorageAccessor: LocalStorageAccessor = <TItem extends any>(
    key: string,
    mappers?: Partial<LocalStorageMappers<TItem>>,
) => ({
    get: fp.pipe(
        () => window.localStorage.getItem(key),
        serialized => serialized
            ? mappers && mappers.deserialize && mappers.deserialize(serialized)
            : mappers && fp.isFunction(mappers.fallback)
                ? mappers.fallback()
                : mappers && mappers.fallback || null,
    ),
    set: fp.pipe(
        mappers && mappers.serialize || fp.toString,
        (serialized: string) => window.localStorage.setItem(key, serialized),
    ),
});

export const localStorageAccessorJSON = <TItem extends any>(
    key: string,
    fallback?: TItem | (() => TItem),
): LocalStorageAccessors<TItem> =>
    localStorageAccessor<TItem>(key, {
        serialize: JSON.stringify,
        deserialize: value => {
            try {
                return JSON.parse(value);
            } catch (error) {
                return fallback || null;
            }
        },
        fallback,
    });

export function addChildrenProps(children: React.ReactNode, props: object): React.ReactNode;
export function addChildrenProps(children: React.ReactNode[], props: object): React.ReactNode[];
export function addChildrenProps(children: React.ReactNode | React.ReactNode[], props: object) {
    const addProps = (child: React.ReactNode): React.ReactNode => React.isValidElement(child)
        ? React.cloneElement(child, props)
        : child;
    if (fp.isArray(children))
        return children.map(addProps);
    return addProps(children);
}

export const fileUrl = (url: string) => url.replace('./',
    `${config.basePath || ''}${config.pathPrefix || ''}/`);

export const scrollToTop = (top: number = 0) => {
    if ('scrollTo' in window) {
        window.scrollTo({
            top,
            behavior: 'smooth',
        });
    } else
        window.scroll(0, top);
};
